import { useEffect, useState } from "react";
import { auth } from "../firebase";
import { supabase } from "../supabase";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
} from "recharts";

const getTier = (score) => {
  if (score >= 85) return { label: "Thriving",    emoji: "🌟", color: "#4a7c6f", light: "#e4f0ed" };
  if (score >= 70) return { label: "Flourishing", emoji: "🌿", color: "#5d9e8e", light: "#e0f2ee" };
  if (score >= 55) return { label: "Stable",      emoji: "🌤️", color: "#e8a135", light: "#fdf4e1" };
  if (score >= 40) return { label: "Struggling",  emoji: "🌧️", color: "#c95b3d", light: "#f9ede9" };
  return              { label: "Needs Care",   emoji: "💙", color: "#7c6fbe", light: "#eeebfa" };
};

const getMessage = (score) => {
  if (score >= 85) return "You've been glowing lately. Your check-ins consistently reflect a bright, positive energy. Whatever you're doing — keep going, it's clearly working.";
  if (score >= 70) return "You're in a genuinely good place. Your recent moods show a healthy rhythm with natural variation. Small consistent habits are clearly paying off for you.";
  if (score >= 55) return "Your mood has been a mix of highs and lows — and that's deeply human. Try noticing what lifts you up and lean into those moments a little more each day.";
  if (score >= 40) return "It looks like you've had a tough stretch. Your check-ins show more low days than high ones. Please be gentle with yourself — explore Resources or chat with Saksham.";
  return "We see you, and we care. Your recent entries suggest you've been carrying something heavy. You don't have to do this alone. Saksham and our Resources are here for you.";
};

function ScoreRing({ score, tier }) {
  const [animated, setAnimated] = useState(0);
  const r = 72;
  const circ = 2 * Math.PI * r;

  useEffect(() => {
    let start = null;
    const duration = 1300;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setAnimated(Math.round(eased * score));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [score]);

  return (
    <div style={{ position: "relative", width: 176, height: 176, margin: "0 auto" }}>
      <svg width="176" height="176" viewBox="0 0 176 176">
        <defs>
          <linearGradient id="wRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={tier.color} />
            <stop offset="100%" stopColor={tier.color + "99"} />
          </linearGradient>
        </defs>
        <circle cx="88" cy="88" r={r} fill="none" stroke="#e2ddd8" strokeWidth="11" />
        <circle
          cx="88" cy="88" r={r}
          fill="none"
          stroke="url(#wRingGrad)"
          strokeWidth="11"
          strokeLinecap="round"
          strokeDasharray={`${(animated / 100) * circ} ${circ}`}
          transform="rotate(-90 88 88)"
          style={{ transition: "stroke-dasharray 0.04s linear" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
      }}>
        <span style={{
          fontFamily: "'DM Serif Display', serif",
          fontSize: 42, lineHeight: 1,
          color: tier.color,
        }}>{animated}</span>
        <span style={{ fontSize: 12, color: "#9c9590", letterSpacing: 1, marginTop: 3 }}>
          out of 100
        </span>
      </div>
    </div>
  );
}

export default function WellnessModal({ onClose }) {
  const [score, setScore]     = useState(null);
  const [history, setHistory] = useState([]);
  const [count, setCount]     = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    (async () => {
      const user = auth.currentUser;
      if (!user) { setLoading(false); return; }

      const { data } = await supabase
        .from("mood_logs")
        .select("score, created_at")
        .eq("user_id", user.uid)
        .order("created_at", { ascending: false })
        .limit(50);

      if (data && data.length > 0) {
        const avg = Math.round(data.reduce((s, r) => s + r.score, 0) / data.length);
        setScore(avg);
        setCount(data.length);
        const slice = data.slice(0, 10).reverse();
        setHistory(slice.map((r, i) => ({ name: `#${i + 1}`, score: r.score })));
      }
      setLoading(false);
    })();
  }, []);

  const tier = score !== null ? getTier(score) : null;

  const low  = history.filter(h => h.score <= 33).length;
  const mid  = history.filter(h => h.score > 33 && h.score <= 66).length;
  const high = history.filter(h => h.score > 66).length;
  const pieData   = [{ name: "Low", value: low }, { name: "Moderate", value: mid }, { name: "High", value: high }];
  const pieColors = ["#e57373", "#e8a135", "#4a7c6f"];

  return (
    <>
      <style>{`
        @keyframes wmFadeUp {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes wmBackdrop { from { opacity: 0; } to { opacity: 1; } }
        .wm-backdrop { animation: wmBackdrop 0.22s ease both; }
        .wm-card     { animation: wmFadeUp 0.4s cubic-bezier(.16,1,.3,1) both; }
        .wm-s1 { animation: wmFadeUp 0.4s ease 0.05s both; }
        .wm-s2 { animation: wmFadeUp 0.4s ease 0.13s both; }
        .wm-close:hover { background: #f5f3f0 !important; color: #1a1714 !important; }
        .wm-card::-webkit-scrollbar { width: 5px; }
        .wm-card::-webkit-scrollbar-thumb { background: #e2ddd8; border-radius: 99px; }
      `}</style>

      <div
        className="wm-backdrop"
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 999,
          background: "rgba(26,23,20,0.55)",
          backdropFilter: "blur(7px)",
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }}
      >
        <div
          className="wm-card"
          onClick={e => e.stopPropagation()}
          style={{
            width: "100%", maxWidth: 640,
            maxHeight: "90vh", overflowY: "auto",
            background: "#ffffff",
            borderRadius: 28,
            border: "1px solid #e2ddd8",
            boxShadow: "0 8px 32px rgba(26,23,20,0.16)",
            fontFamily: "'DM Sans', sans-serif",
            position: "relative",
          }}
        >
          <button
            className="wm-close"
            onClick={onClose}
            style={{
              position: "absolute", top: 16, right: 16,
              width: 34, height: 34, borderRadius: "50%",
              border: "1.5px solid #e2ddd8",
              background: "#fff",
              color: "#9c9590",
              cursor: "pointer", fontSize: 15,
              display: "flex", alignItems: "center", justifyContent: "center",
              transition: "all 0.15s ease",
              zIndex: 10,
            }}
          >✕</button>

          {/* HEADER */}
          <div style={{
            padding: "44px 40px 36px",
            borderBottom: "1px solid #ede9e4",
            background: tier ? tier.light : "#faf8f5",
            borderRadius: "28px 28px 0 0",
            textAlign: "center",
          }}>
            <p style={{
              fontSize: 10, letterSpacing: 3, fontWeight: 700,
              color: "#9c9590", textTransform: "uppercase", marginBottom: 8,
            }}>
              Wellness Score
            </p>
            <h2 style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 24, color: "#1a1714",
              fontWeight: 400, marginBottom: 30, lineHeight: 1.3,
            }}>
              How have you been feeling?
            </h2>

            {loading ? (
              <p style={{ color: "#9c9590", fontSize: 14, padding: "20px 0" }}>Calculating your score...</p>
            ) : score === null ? (
              <div style={{ padding: "20px 0" }}>
                <p style={{ color: "#9c9590", fontSize: 14, lineHeight: 1.8 }}>
                  No mood data yet. Submit your mood slider a few times<br />and your wellness score will appear here.
                </p>
              </div>
            ) : (
              <>
                <ScoreRing score={score} tier={tier} />

                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "#fff",
                  border: `1.5px solid ${tier.color}55`,
                  borderRadius: 9999,
                  padding: "7px 20px",
                  marginTop: 20,
                  boxShadow: "0 1px 3px rgba(26,23,20,0.06)",
                }}>
                  <span style={{ fontSize: 18 }}>{tier.emoji}</span>
                  <span style={{ fontWeight: 700, fontSize: 14, color: tier.color }}>
                    {tier.label}
                  </span>
                </div>

                <p style={{ fontSize: 11, color: "#9c9590", marginTop: 10 }}>
                  Based on your last {count} mood {count === 1 ? "entry" : "entries"}
                </p>

                <p style={{
                  marginTop: 16, fontSize: 14, lineHeight: 1.8,
                  color: "#6b6560", maxWidth: 440, margin: "16px auto 0",
                }}>
                  {getMessage(score)}
                </p>
              </>
            )}
          </div>

          {/* CHARTS */}
          {!loading && score !== null && history.length > 0 && (
            <div style={{ padding: "28px 40px 40px" }}>

              <div className="wm-s1" style={{ marginBottom: 28 }}>
                <p style={{
                  fontSize: 10, letterSpacing: 2.5, fontWeight: 700,
                  color: "#9c9590", textTransform: "uppercase", marginBottom: 14,
                }}>
                  Mood Trend — Last {history.length} Check-ins
                </p>
                <div style={{
                  background: "#faf8f5",
                  border: "1px solid #ede9e4",
                  borderRadius: 16,
                  padding: "16px 8px 8px",
                }}>
                  <ResponsiveContainer width="100%" height={155}>
                    <AreaChart data={history} margin={{ top: 4, right: 12, bottom: 0, left: -22 }}>
                      <defs>
                        <linearGradient id="wAreaG" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={tier.color} stopOpacity={0.22} />
                          <stop offset="95%" stopColor={tier.color} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ede9e4" />
                      <XAxis dataKey="name" tick={{ fill: "#9c9590", fontSize: 11, fontFamily: "DM Sans" }} axisLine={false} tickLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fill: "#9c9590", fontSize: 11, fontFamily: "DM Sans" }} axisLine={false} tickLine={false} />
                      <Tooltip
                        contentStyle={{
                          background: "#fff", border: "1px solid #e2ddd8",
                          borderRadius: 10, color: "#1a1714",
                          fontSize: 13, fontFamily: "DM Sans",
                          boxShadow: "0 2px 8px rgba(26,23,20,0.08)",
                        }}
                        cursor={{ stroke: "#e2ddd8" }}
                      />
                      <Area
                        type="monotone" dataKey="score"
                        stroke={tier.color} strokeWidth={2.5}
                        fill="url(#wAreaG)"
                        dot={{ fill: tier.color, r: 4, strokeWidth: 0 }}
                        activeDot={{ r: 6, fill: tier.color, stroke: "#fff", strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="wm-s2">
                <p style={{
                  fontSize: 10, letterSpacing: 2.5, fontWeight: 700,
                  color: "#9c9590", textTransform: "uppercase", marginBottom: 14,
                }}>
                  Mood Distribution
                </p>
                <div style={{
                  background: "#faf8f5",
                  border: "1px solid #ede9e4",
                  borderRadius: 16,
                  padding: "20px 24px",
                  display: "flex", alignItems: "center",
                  gap: 20, flexWrap: "wrap",
                }}>
                  <ResponsiveContainer width={150} height={150}>
                    <PieChart>
                      <Pie
                        data={pieData} dataKey="value"
                        innerRadius={42} outerRadius={68}
                        paddingAngle={4} startAngle={90} endAngle={-270}
                      >
                        {pieData.map((_, i) => (
                          <Cell key={i} fill={pieColors[i]} stroke="transparent" />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          background: "#fff", border: "1px solid #e2ddd8",
                          borderRadius: 10, color: "#1a1714",
                          fontSize: 13, fontFamily: "DM Sans",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>

                  <div style={{ flex: 1, minWidth: 130 }}>
                    {pieData.map((d, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                        <div style={{ width: 10, height: 10, borderRadius: "50%", background: pieColors[i], flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1714" }}>{d.name} Mood</div>
                          <div style={{ fontSize: 11, color: "#9c9590" }}>
                            {d.value} {d.value === 1 ? "entry" : "entries"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </>
  );
}